import React, { useEffect, useState, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProjectKeyService from 'service/ClientTransmoraService/ProjectKeyService';
import LoadingSpinner from 'component/LoadingSpinner';
import CustomDropdown from 'component/CustomDropdown';

interface KeyItem {
  id: string | null;
  code: string;
  name: string;
  referenceFactId: string;
  keyType: string;
  exportAutoSize: boolean;
}

const ProjectKeyForm: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { profile } = useSelector((state: any) => state.auth);
  const projectKeyService = ProjectKeyService();
  const [keyTypeOptions, setKeyTypeOptions] = useState<string[]>([]);

  const [keys, setKeys] = useState<KeyItem[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  // Drag state
  const dragIndex = useRef<number | null>(null);
  const dragOverIndex = useRef<number | null>(null);

  const [showDeleteKeyModal, setShowDeleteKeyModal] = useState(false);
  const [selectedDeleteIndex, setSelectedDeleteIndex] = useState<number | null>(null);

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token || !projectId) return;

    setIsLoadingDetail(true);
    try {
      const res = await projectKeyService.ProjectKeyDetail(token, projectId);
      const list = res.data?.projectKeyList ?? [];
      setKeys(list.map((k: any) => ({
        id: k.id,
        code: k.code,
        name: k.name,
        referenceFactId: k.referenceFactId ?? '',
        keyType: k.keyType,
        exportAutoSize: k.exportAutoSize,
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const fetchConfig = async () => {
    const token = profile?.data.token;
    if (!token) return;
    try {
      const res = await projectKeyService.ProjectKeyConfig(token);
      setKeyTypeOptions(res.keyTypeList ?? []);
    } catch (err) {
      console.error('Failed to fetch key config:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchConfig();
      await fetchDetail();
    };
    init();
  }, []);

  const handleAddKey = () => {
    setKeys((prev) => [
      ...prev,
      {
        id: null,
        code: '',
        name: '',
        referenceFactId: '',
        keyType: '',
        exportAutoSize: true,
      },
    ]);
  };

  const handleChange = (index: number, field: keyof KeyItem, value: any) => {
    setKeys((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  // Drag handlers
  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragEnter = (index: number) => {
    dragOverIndex.current = index;
    setKeys((prev) => {
      if (dragIndex.current === null || dragIndex.current === index) return prev;
      const updated = [...prev];
      const dragged = updated.splice(dragIndex.current, 1)[0];
      updated.splice(index, 0, dragged);
      dragIndex.current = index;
      return updated;
    });
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
    dragOverIndex.current = null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = profile?.data.token;
    if (!token || !projectId) return;

    setIsLoadingSave(true);
    try {
      const payload = {
        projectId,
        keys: keys.map((item, index) => ({
          id: item.id || "",
          code: item.code,
          name: item.name,
          referenceFactId: item.referenceFactId || "",
          keyType: item.keyType,
          sort: index + 1,
          exportAutoSize: item.exportAutoSize,
        })),
      };

      await projectKeyService.ProjectKeySave(token, payload);
      alert('Project Key berhasil disimpan.');
      navigate('/client-project-key');
    } catch (err: any) {
      alert(err.message || 'Gagal menyimpan data.');
      console.error(err);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const handleDeleteKeyClick = (index: number) => {
    setSelectedDeleteIndex(index);
    setShowDeleteKeyModal(true);
  };

  const handleConfirmDeleteKey = () => {
    if (selectedDeleteIndex === null) return;
    setKeys((prev) => prev.filter((_, i) => i !== selectedDeleteIndex));
    setShowDeleteKeyModal(false);
    setSelectedDeleteIndex(null);
  };

  const handleCloseDeleteKeyModal = () => {
    setShowDeleteKeyModal(false);
    setSelectedDeleteIndex(null);
  };

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">Manage Project Key</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleSubmit}>

              {/* Add Key Button */}
              <div className="d-flex justify-content-end mb-3">
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleAddKey}
                >
                  Add Key
                </button>
              </div>

              {/* Key Cards */}
              <div className="mb-4">
                {keys.length === 0 && (
                  <p className="text-muted text-center fst-italic py-4">
                    Belum ada key. Klik "Add Key" untuk menambahkan.
                  </p>
                )}

                {keys.map((item, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    className="d-flex align-items-center mb-3"
                    style={{ cursor: 'grab' }}
                  >
                    {/* Drag handle */}
                    <div className="me-2 text-secondary" style={{ fontSize: '18px', cursor: 'grab' }}>
                      <i className="fa-solid fa-grip-vertical"></i>
                    </div>

                    {/* Card */}
                    <div
                      className="flex-grow-1 p-3"
                      style={{
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        background: 'white',
                      }}
                    >
                      {/* Row 1: Code + Name */}
                      <div className="row mb-2">
                        <div className="col-sm-3">
                          <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Code</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.code}
                            onChange={(e) => handleChange(index, 'code', e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-sm-9">
                          <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Name</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.name}
                            onChange={(e) => handleChange(index, 'name', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      {/* Row 2: Reference Fact ID + Key Type + Export Auto Size + Delete */}
                      <div className="row align-items-center">
                        <div className="col-sm-3">
                          <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Reference Fact ID</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.referenceFactId}
                            onChange={(e) => handleChange(index, 'referenceFactId', e.target.value)}
                          />
                        </div>
                        <div className="col-sm-3">
                          <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>Key Type</label>
                          {/* <select
                            className="form-select form-select-sm"
                            value={item.keyType}
                            onChange={(e) => handleChange(index, 'keyType', e.target.value)}
                            required
                            >
                            <option value="">-- Pilih --</option>
                            {keyTypeOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                                </option>
                            ))}
                          </select> */}
                          <div>
                            <CustomDropdown
                              value={item.keyType}
                              options={keyTypeOptions.map((opt) => ({
                                id: opt,
                                name: opt.charAt(0).toUpperCase() + opt.slice(1),
                              }))}
                              onChange={(opt) => {
                                handleChange(index, 'keyType', opt.id ? String(opt.id) : "");
                              }}
                              containerClassName="me-1"
                              align="right"
                              buttonStyle={{ width: "160" }}
                            />
                          </div>
                        </div>
                        <div className="col-sm-4 d-flex align-items-center mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input me-2"
                            id={`exportAutoSize-${index}`}
                            checked={item.exportAutoSize}
                            onChange={(e) => handleChange(index, 'exportAutoSize', e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`exportAutoSize-${index}`}
                            style={{ fontSize: '13px' }}
                          >
                            Export Auto Size
                          </label>
                        </div>
                        <div className="col-sm-2 d-flex justify-content-end align-items-center mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm border-0"
                            onClick={() => handleDeleteKeyClick(index)}
                            title="Hapus key"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="w-100 d-flex justify-content-end">
                <Link to="/client-project-key">
                  <button type="button" className="btn btn-outline-primary">
                    Cancel
                  </button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary d-flex justify-content-center align-items-center ms-2"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave && (
                    <>
                      <LoadingSpinner reverse={true} />
                      <p className="ms-2"></p>
                    </>
                  )}
                  Save
                </button>
              </div>

            </form>
          )}
        </div>
      </div>

      {showDeleteKeyModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={handleCloseDeleteKeyModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content p-4">
              <h5 className="fw-bold mb-1">Hapus Key</h5>
              <hr />
              <p className="mb-0">
                Apakah Anda yakin ingin menghapus key{' '}
                {selectedDeleteIndex !== null && keys[selectedDeleteIndex]?.name && (
                  <strong>"{keys[selectedDeleteIndex].name}"</strong>
                )}?
              </p>
              <p className="text-danger small mt-1 mb-0">
                Tindakan ini tidak dapat dibatalkan.
              </p>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={handleCloseDeleteKeyModal}
                  style={{ borderRadius: '8px' }}
                >
                  Batal
                </button>
                <button
                  className="btn btn-danger px-4"
                  onClick={handleConfirmDeleteKey}
                  style={{ borderRadius: '8px' }}
                >
                  <i className="fa-solid fa-trash me-2"></i>
                  Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectKeyForm;